/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import axios from "axios";
import { toast } from "sonner";
import {
  Newspaper, FileText, Microscope, Leaf, ShieldCheck, Plus, PencilSimple,
  Trash, X, FloppyDisk, MagicWand, Image as ImageIcon, Eye, ArrowSquareOut,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("rescigo_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return d || e?.message || "Error";
};

const TABS = [
  { key: "newsroom", label: "Newsroom", Icon: Newspaper, color: "#0A2540" },
  { key: "white_paper", label: "White Papers", Icon: FileText, color: "#2D5A27" },
  { key: "research", label: "Research Reports", Icon: Microscope, color: "#0A2540" },
  { key: "csr_report", label: "CSR Publications", Icon: Leaf, color: "#2D5A27" },
  { key: "security_doc", label: "Security Docs", Icon: ShieldCheck, color: "#B12A1E" },
];

const EMPTY = (kind) => ({
  kind, title: "", subtitle: "", category: "", cover_url: "", pdf_url: "",
  author: "", summary: "", body_html: "", highlights: [], cta_label: "",
  cta_url: "", seo_title: "", seo_description: "", seo_keywords: "",
  status: "draft", audience: "public", download_gate: false, featured: false,
});

export default function ContentManagement() {
  const [tab, setTab] = useState("newsroom");
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/publications", { params: { kind: tab, ...(status ? { status } : {}) } });
      setRows(r.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, [tab, status]);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/publications/${data.id}`, data);
      else await api.post("/admin/publications", data);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this publication?")) return;
    try { await api.delete(`/admin/publications/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const meta = TABS.find(t => t.key === tab);

  return (
    <AppShell>
      <div className="p-6 md:p-8 space-y-6" data-testid="content-management">
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">Content Management</div>
            <h1 className="font-heading text-3xl mt-1 text-slate-900">Publications</h1>
            <p className="text-sm text-slate-600 mt-1">Newsroom, white papers, research, CSR reports & security docs.</p>
          </div>
          <button onClick={() => setEditing(EMPTY(tab))} data-testid="pub-new"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md text-white text-sm font-medium bg-[#0A2540] hover:bg-[#0E3358]">
            <Plus size={13}/> New {meta?.label}
          </button>
        </header>

        {/* Tabs */}
        <div className="flex items-center gap-1 flex-wrap border-b border-slate-200">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`pub-tab-${t.key}`}
                    className={`inline-flex items-center gap-2 px-4 h-11 text-sm font-medium transition border-b-2 ${
                      tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}>
              <t.Icon size={14} weight={tab === t.key ? "duotone" : "regular"}/>{t.label}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div className="flex flex-wrap items-center gap-2">
          {["", "draft", "review", "published", "archived"].map(s => (
            <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`pub-filter-${s || "all"}`}
                    className={`h-8 px-3 rounded-full text-xs font-medium ${
                      status === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}>
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All statuses"}
            </button>
          ))}
          <div className="ml-auto text-xs text-slate-500">{rows.length} publication(s)</div>
        </div>

        {/* Table */}
        <section className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                <th className="p-3 text-left font-semibold">Title</th>
                <th className="p-3 text-left font-semibold">Category</th>
                <th className="p-3 text-left font-semibold">Author</th>
                <th className="p-3 text-left font-semibold">Status</th>
                <th className="p-3 text-left font-semibold">Views</th>
                <th className="p-3 text-left font-semibold">Downloads</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pub-row-${p.id}`}>
                  <td className="p-3">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      {p.featured && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 uppercase tracking-[0.15em] font-bold">Featured</span>}
                      {p.title}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">/{p.slug}</div>
                  </td>
                  <td className="p-3 text-xs text-slate-600">{p.category || "—"}</td>
                  <td className="p-3 text-xs text-slate-600">{p.author || "—"}</td>
                  <td className="p-3"><StatusPill s={p.status}/></td>
                  <td className="p-3 text-xs text-slate-600">{p.view_count || 0}</td>
                  <td className="p-3 text-xs text-slate-600">{p.download_count || 0}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {p.status === "published" && (
                      <a href={publicUrl(p)} target="_blank" rel="noreferrer"
                         className="inline-flex w-7 h-7 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900 items-center justify-center"
                         title="View public">
                        <ArrowSquareOut size={12}/>
                      </a>
                    )}
                    <IconBtn onClick={() => setEditing(p)} title="Edit"><PencilSimple size={12}/></IconBtn>
                    <IconBtn onClick={() => remove(p.id)} title="Delete" red><Trash size={12}/></IconBtn>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-sm text-slate-500 italic">
                  No {meta?.label.toLowerCase()} yet. Click <b>New {meta?.label}</b> to add your first piece.
                </td></tr>
              )}
            </tbody>
          </table>
        </section>

        {editing && <PubModal data={editing} onSave={save} onClose={() => setEditing(null)}/>}
      </div>
    </AppShell>
  );
}

const publicUrl = (p) => {
  const map = { newsroom: "/newsroom", white_paper: "/white-papers",
                 research: "/white-papers", csr_report: "/csr-sustainability",
                 security_doc: "/security-compliance" };
  return `${map[p.kind] || "/newsroom"}/${p.slug}`;
};

// ============================================================================
// Edit/Create Modal
// ============================================================================
function PubModal({ data, onSave, onClose }) {
  const [f, setF] = useState(data);
  const [tab, setTab] = useState("content");
  const [genBusy, setGenBusy] = useState(false);
  const [genPrompt, setGenPrompt] = useState("");

  const generateImage = async () => {
    if (!genPrompt.trim()) { toast.error("Write a prompt first"); return; }
    setGenBusy(true);
    try {
      const r = await api.post("/admin/media/generate", {
        prompt: genPrompt, aspect: "16:9",
      });
      const backendBase = process.env.REACT_APP_BACKEND_URL;
      setF({ ...f, cover_url: `${backendBase}${r.data.url}` });
      toast.success("Image generated");
    } catch (e) { toast.error(fmtErr(e)); }
    setGenBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="pub-modal">
      <div className="bg-white rounded-xl w-full max-w-4xl shadow-2xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{f.id ? "Edit" : "Create"} Publication</div>
            <h2 className="font-heading text-xl mt-0.5 text-slate-900">{f.title || "New publication"}</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded grid place-items-center hover:bg-slate-100"><X size={16}/></button>
        </div>

        <div className="px-6 pt-4 border-b border-slate-100 flex gap-1">
          {["content", "media", "seo", "publish"].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`pub-modal-tab-${t}`}
                    className={`px-4 h-10 text-sm font-medium border-b-2 capitalize ${
                      tab === t ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}>{t}</button>
          ))}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="p-6 space-y-5">
          {tab === "content" && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <F label="Kind" type="select" v={f.kind} on={(v) => setF({ ...f, kind: v })}
                   opts={[["newsroom", "Newsroom"], ["white_paper", "White Paper"], ["research", "Research"], ["csr_report", "CSR Report"], ["security_doc", "Security Doc"]]}/>
                <F label="Category" v={f.category} on={(v) => setF({ ...f, category: v })} placeholder="e.g. BMW Compliance, ESG, Circular Economy"/>
              </div>
              <F label="Title" v={f.title} on={(v) => setF({ ...f, title: v })} required testid="pub-title"/>
              <F label="Subtitle" v={f.subtitle} on={(v) => setF({ ...f, subtitle: v })}/>
              <F label="Author" v={f.author} on={(v) => setF({ ...f, author: v })} placeholder="e.g. Rescigo Research Team"/>
              <F label="Summary" type="textarea" rows={3} v={f.summary} on={(v) => setF({ ...f, summary: v })}/>
              <F label="Body (HTML allowed)" type="textarea" rows={10} v={f.body_html} on={(v) => setF({ ...f, body_html: v })}/>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block mb-1">Key highlights (one per line)</label>
                <textarea value={(f.highlights || []).join("\n")}
                          onChange={(e) => setF({ ...f, highlights: e.target.value.split("\n").filter(x => x.trim()) })}
                          rows={4}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:border-slate-800"/>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <F label="CTA label" v={f.cta_label} on={(v) => setF({ ...f, cta_label: v })} placeholder="e.g. Request consultation"/>
                <F label="CTA URL" v={f.cta_url} on={(v) => setF({ ...f, cta_url: v })} placeholder="/#contact"/>
              </div>
            </div>
          )}

          {tab === "media" && (
            <div className="space-y-5">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block mb-1">Cover image URL</label>
                <div className="flex gap-2">
                  <input value={f.cover_url || ""} onChange={(e) => setF({ ...f, cover_url: e.target.value })}
                         placeholder="https://…/cover.jpg"
                         className="flex-1 h-10 px-3 border border-slate-300 rounded-md text-sm outline-none focus:border-slate-800"/>
                  {f.cover_url && (<a href={f.cover_url} target="_blank" rel="noreferrer" className="h-10 px-3 inline-flex items-center gap-1 rounded border border-slate-300 text-sm"><Eye size={12}/></a>)}
                </div>
                {f.cover_url && <img src={f.cover_url} alt="" className="mt-3 max-h-48 rounded-lg border border-slate-200"/>}
              </div>

              <div className="bg-gradient-to-br from-[#2D5A27]/5 to-[#0A2540]/5 rounded-xl p-5 border border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <MagicWand size={16} weight="duotone" className="text-[#2D5A27]"/>
                  <div className="font-medium text-sm">Generate with AI (Nano Banana)</div>
                </div>
                <textarea value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)}
                          placeholder="e.g. Premium government dashboard visualization with India map, subtle green heat-map overlay, clean enterprise UI, 16:9 hero image"
                          rows={3}
                          data-testid="pub-gen-prompt"
                          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:border-slate-800 bg-white"/>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[11px] text-slate-500">AI-generated image will be saved and attached as the cover.</div>
                  <button type="button" onClick={generateImage} disabled={genBusy}
                          data-testid="pub-gen-btn"
                          className="h-9 px-4 rounded-md text-white text-xs font-medium inline-flex items-center gap-2 bg-[#2D5A27] hover:bg-[#234920] disabled:opacity-60">
                    <MagicWand size={11}/> {genBusy ? "Generating…" : "Generate"}
                  </button>
                </div>
              </div>

              <F label="PDF URL (for white papers / reports)" v={f.pdf_url} on={(v) => setF({ ...f, pdf_url: v })}
                 placeholder="https://…/file.pdf" testid="pub-pdf"/>

              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={!!f.download_gate}
                         onChange={(e) => setF({ ...f, download_gate: e.target.checked })}
                         data-testid="pub-gate"
                         className="accent-slate-900"/>
                  Require email capture before PDF download (CRM lead)
                </label>
              </div>
            </div>
          )}

          {tab === "seo" && (
            <div className="space-y-4">
              <F label="SEO Title" v={f.seo_title} on={(v) => setF({ ...f, seo_title: v })} placeholder="Overrides page title if set"/>
              <F label="Meta description" type="textarea" rows={3} v={f.seo_description} on={(v) => setF({ ...f, seo_description: v })}
                 placeholder="150–160 char preview in search results"/>
              <F label="Keywords (comma separated)" v={f.seo_keywords} on={(v) => setF({ ...f, seo_keywords: v })}
                 placeholder="biomedical waste, BMW compliance, ESG"/>
            </div>
          )}

          {tab === "publish" && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <F label="Status" type="select" v={f.status} on={(v) => setF({ ...f, status: v })}
                   testid="pub-status"
                   opts={[["draft", "Draft"], ["review", "In Review"], ["published", "Published"], ["archived", "Archived"]]}/>
                <F label="Audience" type="select" v={f.audience} on={(v) => setF({ ...f, audience: v })}
                   opts={[["public", "Public"], ["government", "Government Only"], ["partner", "Partners Only"]]}/>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!f.featured}
                       onChange={(e) => setF({ ...f, featured: e.target.checked })}
                       data-testid="pub-featured"
                       className="accent-slate-900"/>
                Featured · pin to the top of the public listing
              </label>
            </div>
          )}

          <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
            <div className="flex-1"/>
            <button type="button" onClick={onClose} className="h-10 px-4 text-sm text-slate-600">Cancel</button>
            <button type="submit" data-testid="pub-save"
                    className="h-10 px-5 rounded-md text-white text-sm font-medium inline-flex items-center gap-2 bg-[#0A2540] hover:bg-[#0E3358]">
              <FloppyDisk size={13} weight="fill"/> Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Primitives
// ============================================================================
const StatusPill = ({ s }) => {
  const map = {
    draft: { bg: "#E2E8F0", color: "#334155", label: "Draft" },
    review: { bg: "#FEF3C7", color: "#92400E", label: "In Review" },
    published: { bg: "#D4E0D1", color: "#1A3317", label: "Published" },
    archived: { bg: "#F4C7B9", color: "#7A2E18", label: "Archived" },
  };
  const m = map[s] || map.draft;
  return <span className="px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold" style={{ background: m.bg, color: m.color }}>{m.label}</span>;
};

const IconBtn = ({ onClick, children, title, red }) => (
  <button type="button" onClick={onClick} title={title}
          className={`w-7 h-7 rounded inline-flex items-center justify-center ml-1 ${red ? "hover:bg-red-50 text-slate-500 hover:text-red-600" : "hover:bg-slate-100 text-slate-500 hover:text-slate-900"}`}>
    {children}
  </button>
);

const F = ({ label, v, on, type = "text", rows = 3, opts, required, placeholder, testid }) => {
  if (type === "select") return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block mb-1">{label}</label>
      <select value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testid}
              className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm bg-white outline-none focus:border-slate-800">
        {opts.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
      </select>
    </div>
  );
  if (type === "textarea") return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block mb-1">{label}</label>
      <textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={rows} data-testid={testid}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:border-slate-800"/>
    </div>
  );
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block mb-1">{label}</label>
      <input type={type} value={v || ""} onChange={(e) => on(e.target.value)}
             placeholder={placeholder} required={required} data-testid={testid}
             className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm outline-none focus:border-slate-800"/>
    </div>
  );
};
